/**
 * Verifies a passphrase by comparing the derived encryptionKey or hash with the backend value.
 * @param {string} passphrase - The user input passphrase to verify
 * @param {string} backendKey - The encryptionKey or hash from the backend to compare
 * @param {'encryptionKey'|'passphraseHashed'} [compareField='encryptionKey'] - Which field to compare
 * @returns {Promise<{ valid: boolean, data: any }>} - valid: true if match, data: result of setUpEncryptionWithPassphrase
 * This is a security-critical function that ensures only authorized users can access encrypted data.
 * The function supports comparing either the encryption key or hashed passphrase based on needs.
 */
import { useI18n } from "vue-i18n";
import moment from "moment";
import Cookies from "js-cookie";
import { StatusCodes } from "http-status-codes";
import { ref, computed } from "vue";
import { errorMessage } from "./errorMessages.js";
import errorImage from "../assets/images/default-placeholder.jpg";
import { isFileField } from "./customFieldMapping.js";
import defaultImageSrc from "../assets/images/portrait-woman.png";
import forge from "node-forge";
import { chacha20poly1305 } from "@noble/ciphers/chacha";
import {
  setupEncryptionV2,
  unwrapV2,
  rewrapToV2,
  isV2Envelope,
  WRAP_VERSION,
} from "./encryptionV2.js";
import { deleteAccountSvg } from "./commonSvg.js";
import { securityModule, commonLabels } from "./label.js";
import { ROUTE_PATHS } from "@/router/constants.js";
import { performTokenRefresh, clearAuthTokens } from "./tokenUtils.js";
import { BaseURL } from "@/api/apiService.js";
import { helpers } from "@vuelidate/validators";
import i18n from "@/i18n";
import { formatSlugToTitle } from "./stringFormatters.js";
import { getI18nTranslation } from "./i18nHelper.js";

// Re-export for convenience
export { getI18nTranslation };

// Default image assets used throughout the application for consistency
export const defaultIconImage =
  "https://cdn.lved.com/assets/data_templates/icons/default.png";
export const defaultImage = defaultImageSrc;

/**
 * Determines if an HTTP status code indicates a successful operation.
 * This centralizes success status checking to ensure consistent behavior across the app.
 */
export const checkStatusCodeSuccess = (status) =>
  status === StatusCodes.OK ||
  status === StatusCodes.ACCEPTED ||
  status === StatusCodes.CREATED;

/**
 * Identifies client-side HTTP errors.
 * Used to distinguish between client errors and server errors for appropriate handling.
 */
export const checkStatusCodeClientError = (status) =>
  status === StatusCodes.BAD_REQUEST ||
  status === StatusCodes.FORBIDDEN ||
  status === StatusCodes.NOT_FOUND ||
  status === StatusCodes.METHOD_NOT_ALLOWED;

/**
 * Converts seconds to a formatted MM:SS string for media duration display.
 */
export function formatSecondsToMMSS(seconds) {
  return moment.utc(seconds * 1000).format("mm:ss");
}

/**
 * Formats duration in seconds to HH:MM format if hours > 0, otherwise MM:SS.
 * @param {number} seconds - Duration in seconds
 * @returns {string} - Formatted duration string
 */
export const formatDuration = (seconds) => {
  if (!seconds) return "";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.round(seconds % 60);

  if (h > 0) {
    return `${h.toString().padStart(2, "0")}:${m
      .toString()
      .padStart(2, "0")}`;
  }
  return `${m}:${s.toString().padStart(2, "0")}`;
};

export function formatDocumentDate(isoString) {
  return moment(isoString).format("D MMMM, YYYY - h:mmA");
}

/**
 * Standardized API response handling across the application.
 * Provides a consistent interface for processing successful and unsuccessful API calls.
 */
export const handleApiResponse = (response) => {
  if (checkStatusCodeSuccess(response?.status)) {
    return { success: true, data: response.data };
  }
  console.error("Unexpected status:", response?.status);
  return { success: false, error: "Unexpected status" };
};

/**
 * Retrieves user-friendly error messages based on error keys.
 * This abstraction allows for consistent error messaging while keeping error codes technical.
 */
export const getErrorMessage = (errorKey) => {
  return errorMessage[errorKey] || "An error occurred";
};

/**
 * Comprehensive error handling that processes various error formats.
 * Handles both system-generated error codes and custom backend error messages.
 * Prioritizes user-friendly messages while preserving technical details for debugging.
 */
export const handleError = (error, allErrorMessages = []) => {
  console.error("handleError called with:", error);
  const errorKey = error?.response?.data?.error;
  let message = getErrorMessage(errorKey);

  // Check for custom error messages from backend
  if (allErrorMessages.length && errorKey) {
    const found = allErrorMessages.find((item) => item.systemCode === errorKey);
    if (found?.message) {
      message = found.message;
    }
  }

  // Fallback to raw message if no error key is available
  if (!errorKey && error?.response?.data?.message) {
    message = error?.response?.data?.message;
  }

  // Final fallback to generic error message
  if (!message) {
    message = "An error occurred";
  }
  return {
    success: false,
    errorMessage: message,
  };
};

/**
 * Masks email addresses for privacy protection in UI displays.
 */
export function maskEmail(email) {
  if (!email) return "";
  const [user, domain] = email.split("@");
  if (!user || !domain) return email;

  // Mask username portion with asterisks, preserving some characters
  const maskedUser =
    user.length <= 4
      ? user[0] + "*".repeat(user.length - 1)
      : user.slice(0, 4) + "*".repeat(user.length - 4);

  // Mask domain name portion similarly
  const [domainName, domainExt] = domain.split(".");
  const maskedDomain =
    domainName.length <= 4
      ? domainName[0] + "*".repeat(domainName.length - 1)
      : domainName.slice(0, 4) + "*".repeat(domainName.length - 4);
  return `${maskedUser}@${maskedDomain}.${domainExt || ""}`;
}

/**
 * Extracts YouTube video IDs from various YouTube URL formats.
 * Supports multiple YouTube URL patterns for robust video embedding.
 */
export function extractYouTubeId(url) {
  const regExp =
    /^.*(?:youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[1].length === 11 ? match[1] : null;
}

/**
 * Standardized marital status options for consistent data collection.
 * Used across forms to ensure uniform data representation.
 */
export const getMaritalStatusOptions = () => {
  return [
    { label: i18n.global.t('commonLabels.maritalStatusOptions.single'), value: "single" },
    { label: i18n.global.t('commonLabels.maritalStatusOptions.married'), value: "married" },
    { label: i18n.global.t('commonLabels.maritalStatusOptions.divorced'), value: "divorced" },
    { label: i18n.global.t('commonLabels.maritalStatusOptions.widowed'), value: "widowed" },
    { label: i18n.global.t('commonLabels.maritalStatusOptions.notSpecified'), value: "not_specified" },
  ];
};

export const getReportedStatusOptions = () => {
  return [
    { label: i18n.global.t('commonLabels.reportedStatusOptions.aliveWell'), value: "alive_well" },
    { label: i18n.global.t('commonLabels.reportedStatusOptions.incapacitated'), value: "incapacitated" },
    { label: i18n.global.t('commonLabels.reportedStatusOptions.deceased'), value: "deceased" },
  ];
};

/**
 * Release timing options.
 */
export const getReleaseTimePeriodOptions = () => {
  return [
    { label: i18n.global.t('commonLabels.releaseTimePeriodOptions.now'), value: "now" },
    { label: i18n.global.t('commonLabels.releaseTimePeriodOptions.incapImmediate'), value: "incap_immediate" },
    {
      label: i18n.global.t('commonLabels.releaseTimePeriodOptions.incapAfterCooling'),
      value: "incap_after_cooling",
    },
    { label: i18n.global.t('commonLabels.releaseTimePeriodOptions.deathImmediate'), value: "death_immediate" },
    { label: i18n.global.t('commonLabels.releaseTimePeriodOptions.deathAfterCooling'), value: "death_after_cooling" },
    { label: i18n.global.t('commonLabels.releaseTimePeriodOptions.deathAfterCertificate'), value: "death_after_certificate" },
    { label: i18n.global.t('commonLabels.releaseTimePeriodOptions.never'), value: "never" },
  ];
};

/**
 * Composable function for managing progress indicators.
 * Provides a standardized way to show progress during asynchronous operations.
 */
export const useProgress = () => {
  const progress = ref(0);
  let progressInterval = null;

  // Starts progress animation with incremental increases
  const startProgress = () => {
    progress.value = 0;
    progressInterval = setInterval(() => {
      if (progress.value < 90) {
        progress.value += 10;
      }
    }, 100);
  };

  // Completes progress by jumping to 100% and cleaning up
  const completeProgress = () => {
    if (progressInterval) {
      clearInterval(progressInterval);
    }
    progress.value = 100;
  };

  // Resets progress to 0 and cleans up any intervals
  const resetProgress = () => {
    if (progressInterval) {
      clearInterval(progressInterval);
    }
    progress.value = 0;
  };
  return {
    progress,
    startProgress,
    completeProgress,
    resetProgress,
    maskEmail,
  };
};

/**
 * Generates standardized cookie expiration time (12 hours from now).
 * Ensures consistent session management across the application.
 */
export function getCookieExpireTime() {
  return new Date(Date.now() + 12 * 60 * 60 * 1000);
}

/**
 * Processes image responses from API calls and creates object URLs.
 * Handles Blob responses and converts them to URLs that can be used in img tags.
 */
export function handleImageResponse(response, mimeType = null) {
  try {
    if (response.data instanceof Blob) {
      // Use provided mimeType or fall back to the Blob's own type
      const type = mimeType || response.data.type || "application/octet-stream";
      const blob = new Blob([response.data], { type });
      return URL.createObjectURL(blob);
    }
    return null;
  } catch (error) {
    console.error("Error handling image response:", error);
    return null;
  }
}

/**
 * Safely revokes object URLs to prevent memory leaks.
 * Important for cleaning up after images are no longer needed.
 */
export function revokeImageUrl(imageUrl) {
  if (imageUrl?.startsWith("blob:")) {
    URL.revokeObjectURL(imageUrl);
  }
}

/**
 * Error handler for image loading failures.
 * Provides fallback image when primary images fail to load.
 */
export const onErrorImage = (event) => {
  event.target.src = errorImage;
  event.target.onerror = null;
};

// Standard date format used throughout the application
export const DATE_FORMAT = "MM/DD/YYYY";
export const DATETIME_FORMAT = "MM-DD-YY - hh:mma";

// Returns the date format string based on the active locale.
// English uses MM/DD/YYYY; Portuguese (pt) uses DD/MM/YYYY.
function getLocaleDateFormat() {
  const locale = i18n.global.locale.value || Cookies.get("language") || "en";
  // Explicitly check for English locale - English uses MM/DD/YYYY
  const isEnglish = locale === "en" || locale === "en-US" || locale === "en-GB";
  return isEnglish ? "MM/DD/YYYY" : "DD/MM/YYYY";
}

/**
 * Formats dates consistently, respecting the active locale.
 * Portuguese locale uses DD/MM/YYYY; default is MM/DD/YYYY.
 */
export function formatDate(date, format) {
  return moment(date).format(format || getLocaleDateFormat());
}

/**
 * Formats datetime strings into "DD/MM/YYYY h:mm A" or "MM/DD/YYYY h:mm A"
 * depending on the active locale.
 */
export function formatDateTime(date) {
  if (!date) return "";
  const m = moment(date); // Moment can parse ISO strings
  if (!m.isValid()) return "";
  return m.local().format(`${getLocaleDateFormat()} h:mm A`);
}

// Formats ISO date (yyyy-mm-dd) to dd/mm/yyyy for cards
export function dateFormat (date)  {
  const d = new Date(date);
  const day = d.getDate().toString().padStart(2, "0");
  const month = (d.getMonth() + 1).toString().padStart(2, "0");
  const year = d.getFullYear();
  return `${month}/${day}/${year}`
}

/**
 * Formats US phone numbers to a standardized +1 XXX XXX XXXX pattern.
 * Handles various input formats and normalizes to consistent output.
 */
export function formatUSPhoneNumber(value) {
  value = value.replace(/[^\d+]/g, "");
  if (value.startsWith("+1")) {
    value = value.slice(2);
  } else if (value.startsWith("+")) {
    value = value.slice(1);
  }
  if (value.length > 10) value = value.slice(0, 10);

  let formatted = "+1";
  if (value.length > 0) formatted += " " + value.slice(0, 3);
  if (value.length > 3) formatted += " " + value.slice(3, 6);
  if (value.length > 6) formatted += " " + value.slice(6, 10);
  return formatted.slice(0, 15);
}

/**
 * Maps status values to appropriate text colors for UI consistency.
 * Provides visual cues for different states throughout the application.
 */
export const StatusTextColor = ref({
  proposed: "text-progress",
  accepted: "text-deepSky",
  declined: "text-alert",
  expired: "text-red",
});

/**
 * Maps status values to user-friendly display text.
 * Translates technical status codes to meaningful UI labels.
 */
export const StatusText = ref({
  proposed: "setPrincipalLabels.proposed",
  pending_authentication: "setPrincipalLabels.authenticationPending",
  accepted: "setPrincipalLabels.accepted",
  declined: "setPrincipalLabels.declined",
  expired: "setPrincipalLabels.expired",
  authentication_pending: "setPrincipalLabels.authenticationPending",
  authentication_verified: "setPrincipalLabels.authenticationAccepted",
});

/**
 * Handles file ID changes and updates both the field data and form state.
 * Ensures consistency between the field object and form values when files change.
 */
export const handleFileIdChanged = ({ fieldName, fileId, fields, form }) => {
  const field = fields?.value?.find((f) => f.fieldName === fieldName);
  if (field) {
    field.fileId = fileId;
    form.value[fieldName] = fileId;
  }
};

/**
 * Utility function to find a field by name in a fields array.
 * Simplifies field lookup operations throughout the application.
 */
export const getField = (fields, name) => {
  return fields?.find((f) => f.fieldName === name);
};

/**
 * Converts field values from frontend format to backend-compatible format.
 * Handles special cases for different field types like files and booleans.
 */
// Helper function for date conversion
function convertDateToBackendFormat(value) {
  // Check if already in DD/MM/YYYY format (backend format)
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    return value;
  }
  
  // Handle YYYY-MM-DD format (from HTML date input)
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
  }
  
  // Handle Date object or ISO string
  const d = new Date(value);
  if (!isNaN(d.getTime())) {
    const day = d.getDate().toString().padStart(2, "0");
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }
  
  // Return as-is if conversion fails
  return value;
}

/**
 * True when a value is wholly an encryption *display placeholder* rather than
 * real field content — e.g. the "[Encrypted - Enter passphrase to view]" string
 * shown for an encrypted field the user hasn't unlocked this session.
 *
 * Such a value must NEVER be persisted: writing it back overwrites the real
 * ciphertext in the backend with human-readable placeholder text, silently and
 * irreversibly destroying the encrypted value. Save-payload builders use this to
 * preserve the stored ciphertext (edit) or drop to empty (create) instead.
 *
 * Matching is exact/anchored, not substring, so legitimate content that merely
 * contains one of these phrases is not mistaken for a placeholder.
 */
export function isEncryptedPlaceholderValue(value) {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (trimmed === "") return false;

  // Bracketed sentinels: "[Encrypted …]", "[Decryption failed …]", etc. These
  // are only ever produced by the decrypt/display layer, never by real input.
  if (/^\[(?:Encrypt|Decrypt).*\]$/.test(trimmed)) return true;

  // Exact translated placeholder strings (active locale + English fallback, so
  // data saved under a different UI language is still caught).
  const t = i18n.global.t;
  const keys = [
    "commonLabels.encryptedPlaceholder",
    "commonLabels.encryptedShort",
    "commonLabels.decryptionFailed",
    "commonLabels.encryptedFilePlaceholder",
  ];
  for (const key of keys) {
    if (trimmed === t(key)) return true;
    if (trimmed === t(key, {}, { locale: "en" })) return true;
  }
  return false;
}

/**
 * HTML-escapes a value for safe interpolation into a string that will be
 * rendered with v-html. Use this on any user-controlled value (e.g. another
 * user's display name) that gets placed inside an i18n template whose result
 * is bound to v-html, so the value cannot inject markup or script.
 */
export function escapeHtml(value) {
  if (value === null || value === undefined) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function convertFieldValueForBackend(value, fieldType) {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return null;
  }

  // Handle object_array - stringify arrays/objects
  if (fieldType === "object_array") {
    if (Array.isArray(value) || typeof value === "object") {
      return JSON.stringify(value);
    }
    return value;
  }

  // Handle date fields
  if (fieldType === "date" && value) {
    return convertDateToBackendFormat(value);
  }

  // Handle boolean fields
  if (fieldType === "boolean") {
    return value ? "true" : "false";
  }

  // Handle number fields
  if (fieldType === "number" && typeof value === "number") {
    return value.toString();
  }

  // For all other types, ensure it's a string
  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
}

/**
 * Converts field values from backend format to frontend-compatible format.
 * Handles type conversion for numbers and booleans to ensure proper UI rendering.
 */
// Helper function for parsing object arrays
function parseObjectArray(value) {
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (e) {
      console.error("Failed to parse object_array value:", e);
      return value; // Return as-is if parsing fails
    }
  }
  return value; // Already parsed
}

// Helper function for converting boolean values
function convertToBoolean(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    return value.toLowerCase() === "true" || value === "1";
  }
  return Boolean(value);
}

// Helper function for date conversion
function convertDateToFrontendFormat(value) {
  // Check if already in YYYY-MM-DD format (HTML date input format)
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  
  // Handle DD/MM/YYYY format (from backend)
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    const [day, month, year] = value.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  
  // Handle MM/DD/YYYY format (alternative backend format)
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value)) {
    return convertAmbiguousDateFormat(value);
  }
  
  // Return as-is if format is unknown
  return value;
}

// Helper function for ambiguous date formats
function convertAmbiguousDateFormat(value) {
  const parts = value.split('/');
  if (parts.length !== 3) {
    return value;
  }
  
  const [first, second, year] = parts;
  // If first part > 12, it's likely day (DD/MM/YYYY)
  if (parseInt(first) > 12) {
    return `${year}-${second.padStart(2, '0')}-${first.padStart(2, '0')}`;
  }
  // Otherwise assume MM/DD/YYYY
  return `${year}-${first.padStart(2, '0')}-${second.padStart(2, '0')}`;
}

export function convertFieldValueForFrontend(value, fieldType) {
  // Handle null/undefined
  if (value === null || value === undefined || value === "") {
    return "";
  }

  // Handle object_array - parse JSON strings
  if (fieldType === "object_array") {
    return parseObjectArray(value);
  }

  // Handle boolean fields
  if (fieldType === "boolean") {
    return convertToBoolean(value);
  }

  // Handle number fields
  if (fieldType === "number") {
    return Number(value);
  }

  // Handle date fields
  if (fieldType === "date" && value) {
    return convertDateToFrontendFormat(value);
  }

  // For all other types, return as-is
  return value;
}

/**
 * Links uploaded files to their corresponding fields in the backend.
 * This establishes the relationship between file records and form fields.
 */
export const linkFileFields = async (
  responseFields,
  fields,
  form,
  objectId,
  linkFileToField
) => {
  // Identify which fields are file fields for processing
  const fileTemplateFieldIds = fields
    .filter((f) => isFileField(f.fieldType))
    .map((f) => f.id);

  // Process each response field to link files if needed
  for (const field of responseFields) {
    if (fileTemplateFieldIds.includes(field.templateFieldId)) {
      const fileId =
        form[fields.find((f) => f.id === field.templateFieldId).fieldName];
      // Only link files that have valid file IDs
      if (fileId && fileId !== 0) {
        await linkFileToField(objectId, field.id, fileId);
      }
    }
  }
};

/**
 * Derives encryption keys from a passphrase using multiple hashing algorithms.
 */
function derivePassphraseKey(passphrase) {
  // Hash passphrase (SHA1)
  const passphraseHashed = forge.md.sha1
    .create()
    .update(passphrase)
    .digest()
    .toHex();

  // Derive ChaCha20 key (from SHA-256 of passphrase hash)
  const sha256 = forge.md.sha256
    .create()
    .update(passphraseHashed)
    .digest()
    .getBytes();
  const chachaKey = new Uint8Array(
    sha256.split("").map((c) => c.charCodeAt(0))
  );
  const nonce = new Uint8Array(12); // fixed nonce for consistent encryption/decryption

  return { passphraseHashed, chachaKey, nonce };
}

/**
 * Sets up the complete encryption system for a user based on their passphrase.
 * Delegates to v2 wrapping (Argon2id + XChaCha20-Poly1305, unique nonce per
 * secret). Keeps the v1 return shape so callers need no changes.
 * See utils/encryptionV2.js and docs/encryption-v2-design.md.
 */
export const setUpEncryptionWithPassphrase = async (passphrase = "") => {
  const info = await setupEncryptionV2(passphrase);
  // Retained for backward compatibility with existing client-only readers of
  // encryptionInfo.passphraseHashed; never sent to the server.
  const passphraseHashed = forge.md.sha1
    .create()
    .update(passphrase)
    .digest()
    .toHex();
  return { ...info, passphraseHashed };
};

/**
 * Legacy v1 unwrap: fixed-nonce ChaCha20-Poly1305. Used only to read
 * accounts created before v2 so they can be migrated. Returns the vault key
 * and, when the private-key blob is supplied, the RSA private key too.
 */
function unwrapV1(passphrase, encryptedEncryptionKey, encryptedPrivateKey = null) {
  const { passphraseHashed, chachaKey, nonce } = derivePassphraseKey(passphrase);
  const decryptHex = (hex) => {
    const bytes = forge.util.hexToBytes(hex);
    const out = chacha20poly1305(chachaKey, nonce).decrypt(
      new Uint8Array(bytes.split("").map((c) => c.charCodeAt(0)))
    );
    return out;
  };
  try {
    const encryptionKey = forge.util.bytesToHex(
      String.fromCharCode(...decryptHex(encryptedEncryptionKey))
    );
    const result = { valid: true, encryptionKey, passphraseHashed };
    if (encryptedPrivateKey) {
      result.rsaPrivateKey = new TextDecoder().decode(
        decryptHex(encryptedPrivateKey)
      );
    }
    return result;
  } catch (err) {
    return { valid: false };
  }
}

/**
 * Verifies a passphrase by attempting to decrypt the stored vault key.
 * Reads either a v2 envelope or a legacy v1 blob transparently. For v1 it
 * sets needsRewrap so the caller can migrate the account to v2 (see
 * securityStore.ensureV2Wrapping).
 *
 * @param {string} passphrase
 * @param {string} backendEncryptedEncryptionKey  wrapped vault key from the API
 * @param {string|null} backendEncryptedPrivateKey optional wrapped RSA key (encryptedKey)
 */
export async function verifyPassphrase(
  passphrase = "",
  backendEncryptedEncryptionKey = "",
  backendEncryptedPrivateKey = null
) {
  const passphraseHashed = forge.md.sha1
    .create()
    .update(passphrase)
    .digest()
    .toHex();

  if (isV2Envelope(backendEncryptedEncryptionKey)) {
    const res = await unwrapV2(
      passphrase,
      backendEncryptedEncryptionKey,
      backendEncryptedPrivateKey
    );
    if (!res.valid) return { valid: false };
    return {
      valid: true,
      encryptionKey: res.encryptionKey,
      rsaPrivateKey: res.rsaPrivateKey,
      passphraseHashed,
      wrapVersion: WRAP_VERSION,
    };
  }

  // Legacy v1 account — verify, then flag for lazy migration to v2.
  const v1 = unwrapV1(
    passphrase,
    backendEncryptedEncryptionKey,
    backendEncryptedPrivateKey
  );
  if (!v1.valid) return { valid: false };
  return { ...v1, wrapVersion: 1, needsRewrap: true };
}

/**
 * Migrate a v1 account's key WRAPPING to v2 without changing the vault key
 * (Tier A in docs/encryption-v2-design.md): decrypt the v1 blobs, then re-wrap
 * the same vault key and RSA keypair under Argon2id + XChaCha20 with unique
 * nonces. This stops all future snapshots from leaking keys.
 *
 * NOTE: this does NOT rotate the vault key, so a v1 blob captured in an older
 * backup still exposes the (still-current) vault key. Full protection also
 * needs vault-key rotation + data re-encryption (Tier B) — tracked separately.
 *
 * @returns new payload fields for POST /me/encryption/status, or null if the
 *   account is already v2 or the passphrase is wrong.
 */
export async function migrateWrappingToV2(passphrase, encryptionDetails = {}) {
  const { encryptedEncryptionKey, encryptedKey, publicKey } = encryptionDetails;
  if (!encryptedEncryptionKey || isV2Envelope(encryptedEncryptionKey)) {
    return null; // nothing to do / already v2
  }
  const unwrapped = unwrapV1(passphrase, encryptedEncryptionKey, encryptedKey);
  if (!unwrapped.valid) return null;

  const rewrapped = await rewrapToV2({
    passphrase,
    encryptionKey: unwrapped.encryptionKey,
    rsaPrivateKey: unwrapped.rsaPrivateKey,
    rsaPublicKey: publicKey,
  });

  return {
    encryptedKey: rewrapped.encryptedPrivateKey,
    encryptedEncryptionKey: rewrapped.encryptedEncryptionKey,
    publicKey: rewrapped.rsaPublicKey,
    encryptionKeyVersion: WRAP_VERSION,
  };
}

/**
 * Creates a debounced function that delays execution until after wait time.
 * Useful for limiting API calls during rapid user input (like search).
 */
export function debounce(fn, delay) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

/**
 * Regex pattern for matching subscription plan keys in notification text
 * Example: subscriptionPlans.1.title, subscriptionPlans.2.description
 */
export const subscriptionPlansRegex = /subscriptionPlans\.\d+\.[a-zA-Z0-9-_]+/g;

/**
 * Function to translate template keys within text
 * @param {string} text - The text containing template keys
 * @param {Function} te - Translation existence checker function
 * @param {Function} t - Translation function
 * @returns {string} - Text with template keys replaced by translations
 */
export const translateTemplateKeys = (text, te, t) => {
  if (!text || typeof text !== 'string') return text;
  
  // Find all template keys in text (e.g., 'templates.child.name')
  const templateKeyRegex = /templates\.[a-zA-Z0-9-_]+(\.[a-zA-Z0-9-_]+)?/g;
  
  return text.replace(templateKeyRegex, (match) => {
    // Check if this template key exists in translations
    if (te(match)) {
      return t(match);
    }
    // Return original if no translation found
    return match;
  });
};

/**
 * Maps technical role codes to user-friendly display names.
 */
export function getRole(role) {
  const roleMap = {
    agent: "Account Agent",
    poa_health: "Health POA",
    poa_financial: "Financial POA",
    health_support: "Health Support",
    executor: "Executor",
    trustee: "Trustee",
    guardian: "Guardian",
    general: "General",
  };
  return roleMap[role] || role || "";
}

/**
 * Maps technical role To route paths for navigation.
 */
export function getRoleRoute(role) {
  const roleMap = {
    agent: ROUTE_PATHS.ACCOUNT_AGENT,
    poa_health: ROUTE_PATHS.HEALTH_POA,
    poa_financial: ROUTE_PATHS.FINANCIAL_POA,
    health_support: ROUTE_PATHS.HEALTH_SUPPORT,
    executor: ROUTE_PATHS.EXECUTOR,
    trustee: ROUTE_PATHS.TRUSTEE,
    guardian: ROUTE_PATHS.GUARDIAN,
  };
  return roleMap[role] || role || "";
}

/**
 * Maps technical role level codes to user-friendly display names.
 * Provides consistent labeling for role hierarchy throughout the UI.
 */
export function getRoleLevel(roleLevel) {
  const roleLevelMap = {
    principal: "Principal",
    "co-principal": "Co-Principal",
    backup: "Backup",
  };
  return roleLevelMap[roleLevel] || roleLevel || "";
}

/**
 * Maps technical status codes to user-friendly display names.
 * Ensures consistent status presentation across the application.
 */
export function getStatus(status) {
  const statusMap = {
    accepted: "Accepted",
    proposed: "Pending",
    expired: "Expired",
    declined: "Declined",
  };
  return statusMap[status] || status || "";
}

/**
 * Get cookie value as a number with a safe default
 * @param {string} key - Cookie name
 * @param {number} [defaultValue=0] - Default value if cookie doesn't exist or is invalid
 * @returns {number} - The parsed number or default value
 */
export const getCookieNumber = (key, defaultValue = 0) => {
  // Check cache first
  if (cookieCache.has(key)) {
    const cached = cookieCache.get(key);
    return typeof cached === "number" ? cached : defaultValue;
  }

  // Get directly from Cookies
  const value = Cookies.get(key);
  if (value === undefined) return defaultValue;

  // Parse and validate number
  const parsed = Number(value);
  const result = !isNaN(parsed) ? parsed : defaultValue;

  // Cache the result
  cookieCache.set(key, result);
  return result;
};

/**
 * Get cookie value as a boolean with a safe default
 * @param {string} key - Cookie name
 * @param {boolean} [defaultValue=false] - Default value if cookie doesn't exist or is invalid
 * @returns {boolean} - The parsed boolean or default value
 */
export const getCookieBoolean = (key, defaultValue = false) => {
  // Check cache first
  if (cookieCache.has(key)) {
    const cached = cookieCache.get(key);
    if (typeof cached === "boolean") return cached;
    if (cached === 1 || cached === "1") return true;
    if (cached === 0 || cached === "0") return false;
  }

  // Get directly from Cookies
  const value = Cookies.get(key);
  if (value === undefined) return Boolean(defaultValue);

  // Handle string representations of boolean
  if (typeof value === "string") {
    const normalized = value.toLowerCase().trim();
    if (["true", "1", "yes"].includes(normalized)) {
      cookieCache.set(key, true);
      return true;
    }
    if (["false", "0", "no"].includes(normalized)) {
      cookieCache.set(key, false);
      return false;
    }
  }

  // Handle number values
  if (
    typeof value === "number" ||
    (typeof value === "string" && !isNaN(Number(value)))
  ) {
    const numValue = Number(value);
    const result = numValue === 1;
    cookieCache.set(key, result);
    return result;
  }

  // Fall back to default value
  return Boolean(defaultValue);
};

// Cache for parsed values
const cookieCache = new Map();
/**
 * Save data to cookie
 * @param {string} key - Cookie name
 * @param {any} value - Value to store (will be stringified if not a string)
 * @param {number|Date} [expires] - Expiration time in days or Date object
 */
export function saveToCookie(key, value, expires) {
  if (value === undefined) return;

  const stringValue = typeof value === "string" ? value : JSON.stringify(value);
  cookieCache.set(key, value);
  Cookies.set(key, stringValue, { expires: expires || getCookieExpireTime() });
}

/**
 * Get cookie with automatic type detection
 */
// Helper function for parsing boolean values
function parseBooleanValue(value, defaultValue) {
  if (value === undefined) return Boolean(defaultValue);
  if (value === true || value === false) return value;
  
  const lowerValue = String(value).toLowerCase().trim();
  if (["true", "1", "yes"].includes(lowerValue)) return true;
  if (["false", "0", "no"].includes(lowerValue)) return false;
  return Boolean(defaultValue);
}

// Helper function for parsing number values
function parseNumberValue(value, defaultValue, key) {
  const parsed = Number(value);
  const result = !isNaN(parsed) ? parsed : defaultValue;
  cookieCache.set(key, result);
  return result;
}

// Helper function for parsing object values
function parseObjectValue(value, defaultValue, key) {
  try {
    const parsed = JSON.parse(value);
    cookieCache.set(key, parsed);
    return parsed;
  } catch {
    return defaultValue;
  }
}

// Helper function for auto-detecting type
function autoDetectType(value, key, defaultValue) {
  // Try JSON parse first
  try {
    const parsed = JSON.parse(value);
    cookieCache.set(key, parsed);
    return parsed;
  } catch {
    // Not JSON, continue with other types
  }

  // Check for boolean/string
  const lowerValue = value.toLowerCase().trim();
  if (["true", "false", "1", "0", "yes", "no"].includes(lowerValue)) {
    return getCookieBoolean(key, defaultValue);
  }

  // Check for number
  if (!isNaN(Number(value)) && value.trim() !== "") {
    return getCookieNumber(key, defaultValue);
  }

  // Default to string
  return value;
}

export function getCookie(key, defaultValue = null, type = "auto") {
  // Check cache first
  if (cookieCache.has(key)) {
    return cookieCache.get(key);
  }

  const value = Cookies.get(key);
  if (value === undefined) return defaultValue;

  // Handle explicit types
  if (type === "object") {
    return parseObjectValue(value, defaultValue, key);
  }
  
  if (type === "string") {
    return value;
  }

  if (type === "boolean") {
    return parseBooleanValue(value, defaultValue);
  }

  if (type === "number") {
    return parseNumberValue(value, defaultValue, key);
  }

  // Auto-detect type
  return autoDetectType(value, key, defaultValue);
}

// Helper function to handle expiration date conversion
function getExpirationDate(expires) {
  if (typeof expires === "number") {
    const expirationDate = new Date();
    expirationDate.setTime(
      expirationDate.getTime() + expires * 24 * 60 * 60 * 1000
    );
    return expirationDate;
  }
  
  if (expires instanceof Date) {
    return expires;
  }
  
  console.warn(
    "setCookie: Invalid expires value, should be number (days) or Date object"
  );
  return null;
}

// Helper function to build cookie attributes
function buildCookieAttributes(options) {
  let attributes = "";

  // Handle expiration
  if (options.expires !== undefined) {
    const expirationDate = getExpirationDate(options.expires);
    if (expirationDate) {
      attributes += `; expires=${expirationDate.toUTCString()}`;
    }
  }

  // Handle max-age (takes precedence over expires)
  if (options.maxAge !== undefined) {
    if (typeof options.maxAge === "number" && options.maxAge >= 0) {
      attributes += `; max-age=${options.maxAge}`;
    } else {
      console.warn(
        "setCookie: Invalid maxAge value, should be a non-negative number"
      );
    }
  }

  // Set path (default to root)
  const path = options.path !== undefined ? options.path : "/";
  attributes += `; path=${path}`;

  // Set domain if specified
  if (options.domain) {
    attributes += `; domain=${options.domain}`;
  }

  // Set secure flag if specified
  if (options.secure === true) {
    attributes += `; secure`;
  }

  // Set SameSite attribute
  if (options.sameSite) {
    const validSameSiteValues = ["Strict", "Lax", "None"];
    if (validSameSiteValues.includes(options.sameSite)) {
      attributes += `; sameSite=${options.sameSite}`;
    } else {
      console.warn(
        'setCookie: Invalid sameSite value, should be "Strict", "Lax", or "None"'
      );
    }
  }

  // Note: HttpOnly cannot be set via JavaScript for security reasons
  if (options.httpOnly) {
    console.warn("setCookie: HttpOnly flag cannot be set via JavaScript");
  }

  return attributes;
}

export function setCookie(name, value, options = {}) {
  if (!name) {
    console.warn("setCookie: Cookie name is required");
    return false;
  }

  // Build the cookie string
  const cookieString = 
    `${encodeURIComponent(name)}=${encodeURIComponent(value || "")}` +
    buildCookieAttributes(options);

  try {
    document.cookie = cookieString;
    return true;
  } catch (error) {
    console.error("setCookie: Failed to set cookie:", error);
    return false;
  }
}

/**
 * Clear all cookies from the browser and internal cache
 * @returns {void}
 */
export function clearAllCookies(excludeKeys = []) {
  const cookies = Cookies.get();
  Object.keys(cookies).forEach((cookieName) => {
    // Skip cookies that should be excluded
    if (excludeKeys.includes(cookieName)) {
      return;
    }

    // Use our saveToCookie with an expired date to remove the cookie
    saveToCookie(cookieName, "", -1);
    cookieCache.delete(cookieName);
  });

  // Only clear cache entries that aren't excluded
  const cacheKeys = Array.from(cookieCache.keys());
  cacheKeys.forEach((key) => {
    if (!excludeKeys.includes(key)) {
      cookieCache.delete(key);
    }
  });
}

/**
 * Convert bytes into a human-readable storage size string
 * @param {number} bytes - The size in bytes
 * @returns {string} - Human-readable format (e.g., "2.35 MB")
 */
export function formatReadableSize(bytes = 0) {
  if (!bytes || bytes <= 0) return "0 KB";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = (bytes / Math.pow(k, i)).toFixed(2);
  return `${parseFloat(value)} ${sizes[i]}`;
}

export function removeCookie(name, options = {}) {
  // Set the cookie with an expiration date in the past to delete it
  const expires = new Date(0); // January 1, 1970

  let cookieString = `${name}=; expires=${expires.toUTCString()}`;

  // Add path if specified
  if (options.path) {
    cookieString += `; path=${options.path}`;
  } else {
    cookieString += `; path=/`; // Default to root path
  }

  // Add domain if specified
  if (options.domain) {
    cookieString += `; domain=${options.domain}`;
  }

  // Add secure flag if specified
  if (options.secure) {
    cookieString += `; secure`;
  }

  // Add sameSite if specified
  if (options.sameSite) {
    cookieString += `; sameSite=${options.sameSite}`;
  }

  document.cookie = cookieString;
}

export function capitalize(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Formats a category name for display.
 * Handles special cases (e.g., "poas" → "Power of Attorney") and converts
 * snake_case to Title Case for all other values.
 *
 * @param {string} text - Raw category name from the API
 * @returns {string} - Human-readable category label
 */
export const formatCategoryName = (text) => {
  if (!text) return "";
  if (text === "poas") {
    return "Power of Attorney";
  }
  // Delegate to the shared formatter for consistency
  return formatSlugToTitle(text);
};

export function useConfirmationDialog(passphraseStatus, securityStore) {
  return computed(() => {
    let props = {};

    const commonDialogProps = {
      svg: deleteAccountSvg(),
      svgStyle: {
        background:
          "linear-gradient(0deg, #FFEAE1, #FFEAE1), linear-gradient(315deg, rgba(251, 143, 0, 0) 11.89%, rgba(251, 143, 0, 0.2) 86.59%)",
      },
      subtitleClass: "",
      error: securityStore.errorMessage,
      loading: securityStore.loading,
      dialogClass:
        "bg-white rounded-2xl w-[95%] sm:w-[90%] md:w-[80%] lg:w-[70%] xl:w-[60%] max-w-[1051px] h-[80vh] sm:h-[85vh] md:h-[90vh] max-h-[694px]",
    };

    switch (passphraseStatus.value) {
      case "wrongPassphrase":// NOSONAR - UI message, not a credential
        props = {
          title: securityModule.dialog.wrongPassphraseTitle,
          subtitle: securityModule.dialog.wrongPassphraseSubtitle,
          okBtnText: securityModule.dialog.loseAllDataBtn,
          cancelBtnText: commonLabels.tryAgain,
        };
        break;

      case "confirmPassphrase":
        props = {
          title: securityModule.dialog.confirmPassphraseTitle,
          subtitle: securityModule.dialog.confirmPassphraseSubtitle,
          okBtnText: securityModule.dialog.restoreMyKeyBtn,
          cancelBtnText: securityModule.dialog.enterMyKeyBtn,
        };
        break;

      default:
        props = {};
    }

    return {
      ...props,
      ...commonDialogProps,
    };
  });
}

// Utility to convert boolean to "yes"/"no"
export const booleanToYesNo = (value) => (value ? "yes" : "no");

// Utility to convert "yes"/"no" back to boolean
export const yesNoToBoolean = (value) => value === "yes";

/**
 * Formats a date string into a localized format
 */
export const formattedDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

export const decodeHtmlEntities = (str) => {
  const txt = document.createElement("textarea");
  txt.innerHTML = str;
  return txt.value;
};

const relationMap = {
  "ex-spouse": "Ex-spouse",
  spouse: "Spouse",
  parent: "Parent",
  sibling: "Sibling",
  child: "Child",
  grandchild: "Grandchild",
  uncle: "Uncle",
  aunt: "Aunt",
  niece: "Niece",
  nephew: "Nephew",
  cousin: "Cousin",
  friend: "Friend",
  business_partner: "Business partner",
  other_family: "Other family",
  other: "Other",
};

export function getRelation(value, toApi = false) {
  if (toApi) {
    // reverse lookup
    const found = Object.entries(relationMap).find(
      ([api, label]) => label === value
    );
    return found ? found[0] : value || "";
  }
  return relationMap[value] || value || "";
}

export const getExpireTime = (expiryDate) => {
  try {
    if (!expiryDate) return "00:00";

    const now = new Date();
    const future = new Date(expiryDate);

    if (isNaN(future.getTime())) return "00:00"; // invalid date fallback

    // Difference in seconds
    let diff = Math.max(0, Math.floor((future - now) / 1000));

    const minutes = String(Math.floor(diff / 60)).padStart(2, "0");
    const seconds = String(diff % 60).padStart(2, "0");

    return `${minutes}:${seconds}`;
  } catch (e) {
    console.error("Error in getExpireTime:", e);
    return "00:00";
  }
};

/**
 * Checks if the token is about to expire and refreshes it if necessary.
 * @returns {Promise<boolean>} - True if the token is valid or was refreshed successfully, false otherwise.
 */
export async function checkAndRefreshToken() {
  const result = await performTokenRefresh(BaseURL, 0); // 0 minutes threshold for router - refresh if expired

  if (!result.success) {
    clearAuthTokens();
    return false;
  }

  return true;
}

export const formatCurrency = (amountInCents, currency = "USD") => {
  if (!amountInCents) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amountInCents / 100);
};

export const validSurname = (fieldName = "Surname") =>
  helpers.withMessage(
    ({ $params, $model }) => {
      // Handle non-primitive values
      if ($model != null && typeof $model === 'object') {
        return i18n.global.t('validation.surnameMustBeText', { fieldName });
      }
      
      const str = String($model ?? "").trim();
      if (!str) return i18n.global.t('validation.surnameRequired', { fieldName });

      const hasLetter = /[A-Za-z]/.test(str);
      const allowedPattern = /^[A-Za-z0-9\s\-.,']+$/;
      if (!hasLetter && /^\d+$/.test(str)) {
        return i18n.global.t('validation.surnameCannotBeNumbersOnly', { fieldName });
      }
      if (!hasLetter && /^[\d\W_]+$/.test(str)) {
        return i18n.global.t('validation.surnameCannotContainOnlyDigitsOrSpecial', { fieldName });
      }
      if (!allowedPattern.test(str)) {
        return i18n.global.t('validation.surnameCannotContainSpecialChars', { fieldName });
      }
      return true;
    },
    (value) => {
      // Reject non-primitive values early
      if (value != null && typeof value === 'object') {
        return false;
      }
      
      // Handle null/undefined or convert to string
      if (value == null) {
        return true; // Empty values are valid (required check is separate)
      }
      
      const str = String(value).trim();
      if (!str) return true;

      const hasLetter = /[A-Za-z]/.test(str);
      const allowedPattern = /^[A-Za-z0-9\s\-.,']+$/;

      return hasLetter && allowedPattern.test(str);
    }
  );

// Helper for transforming individual agent objects to UI format
export function transformAgent(agent) {
  if (!agent) return null;
  
  const name =
    agent.fields?.find((f) => f.fieldName === "Name" || f.fieldName === "Nickname")?.fieldValue ||
    agent.fields?.find((f) => f.fieldName === "FullName")?.fieldValue ||
    agent.title ||
    "";
    
  const image =
    agent.fields?.find((f) => f.fieldName === "Picture")?.fieldValue ||
    defaultImageSrc;
    
  const fileIds =
    agent.fields
      ?.filter((f) => f.fileId && f.fileId !== 0)
      .map((f) => f.fileId) || [];

  return {
    id: agent.id,
    name,
    image,
    fileIds, // Required for ChooseAssets
    email:
      agent.fields?.find((f) => f.fieldName === "Email" || f.fieldName === "email")?.fieldValue || "",
    phoneNumber:
      agent.fields?.find((f) => f.fieldName === "phoneNumber")?.fieldValue ||
      "",
    subtitle: i18n.global.t('commonLabels.tapToSelect') || "Tap to Select",
    tag: agent.dataTemplateName,
    status: agent.status,
    badgeIcon: agent.icon,
    original: agent,
  };
}

/**
 * Transforms beneficiaries list and optionally filters out excluded agents.
 * Replaces both filteredBeneficiaries (deprecated/broken) and duplicate logic in useFilteredBeneficiaries.
 * 
 * @param {Array} beneficiariesList - Source list of beneficiaries
 * @param {Array} excludedAgents - List of agents to exclude
 */
export function useFilteredBeneficiaries(
  beneficiariesList,
  excludedAgents = []
) {
  return computed(() => {
    if (!beneficiariesList) return [];

    // Get IDs of already selected agents
    const excludedIds = excludedAgents
      .map((agent) => agent?.id || agent?.sourceObjectId)
      .filter(Boolean);

    // Filter out selected agents
    const filtered = beneficiariesList.filter(
      (b) => !excludedIds.includes(b?.id)
    );

    // Transform to match UI format
    return filtered.map(transformAgent);
  });
}

/**
 * Transforms beneficiaries list without filtering.
 * Uses shared transformation logic.
 * @param {Array} beneficiariesList - Raw beneficiaries list from store
 * @returns {Object} Computed property with transformed beneficiaries
 */
export function useTransformedBeneficiaries(beneficiariesList) {
  return computed(() => {
    if (!beneficiariesList) return [];
    return beneficiariesList.map(transformAgent);
  });
}

export const getStatusText = (percentComplete) => {
  if (percentComplete === 0) return i18n.global.t("commonLabels.pending");
  if (percentComplete === 100) return i18n.global.t("commonLabels.done");
  return `${percentComplete}%`;
};

export const statusClassResolver = (percentComplete) => {
  if (percentComplete === 0) {
    return "bg-softRose text-alertRed";
  }
  if (percentComplete === 100) {
    return "bg-lightGreen text-darkGreen";
  }
  return "";
};
